import { useEffect, useState } from "react";
import { PdfViewerModel } from "@syncfusion/ej2-react-pdfviewer";
import { PdfTemplateDesignerProps } from "./PdfTemplateDesigner";
import { DefaultHtmlAttributes } from "@syncfusion/ej2-react-base";
import { usePdfTemplateDesigner } from "./usePdfTemplateDesigner";

export const usePdfTemplateDesignerComponent = (
  props: PdfTemplateDesignerProps
) => {
  const [pdfViewerSettings, setPdfViewerSettings] = useState<
    PdfViewerModel & DefaultHtmlAttributes
  >();
  const isReadOnlyMode = !props.onSave;
  const [ready, setReady] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [notSupported, setNotSupported] = useState<boolean>(false);
  const [isPreview, setIsPreview] = useState<boolean>(false);

  // Use the new template designer module
  const templateDesignerModule = usePdfTemplateDesigner(
    props.pdfViewerRef,
    props.onSave,
    props.onPreviewAnnotations,
    props.getTemplateFieldsData,
    props.annotationDefaults
  );

  const defaultPdfViewerSettings: PdfViewerModel & DefaultHtmlAttributes = {
    className: "e-control e-lib e-pdfviewer ",
    zoomMode: "FitToWidth",
    enableStickyNotesAnnotation: true,
    documentLoad: () => {
      handleDocumentLoad();

      // Force resize to help solve height adjustments
      setTimeout(() => {
        window.dispatchEvent(new Event("resize"));
      }, 300);
    },
  };

  // Setup
  useEffect(() => {
    setup();
    return () => {
      templateDesignerModule.reset();
    };
  }, []);

  useEffect(() => {
    if (isPreview) {
      templateDesignerModule.setPreviewMode();
    } else {
      templateDesignerModule.setDesignMode();
    }
  }, [isPreview]); //, templateDesignerModule]);

  // Initialize
  const handleDocumentLoad = () => {
    if (
      props.pdfViewerRef &&
      "current" in props.pdfViewerRef &&
      props.pdfViewerRef.current
    ) {
      templateDesignerModule.initialize(props.fileFormData);
      setNotSupported(
        !!props.onSave && !props.pdfViewerRef.current.annotationModule
      );

      setTimeout(() => {
        setIsLoading(false);
      }, 2000);
    }
  };

  // Setup Method
  const setup = async () => {
    const pdfViewerModel = templateDesignerModule.setup({
      pdfViewerId: props.id || "template-designer-pdfviewer-container",
      isReadOnlyMode: isReadOnlyMode,
    });

    setPdfViewerSettings(pdfViewerModel);
    setReady(true);

    // Force resize to help solve height adjustments
    window.dispatchEvent(new Event("resize"));
  };

  return {
    isLoading,
    defaultPdfViewerSettings,
    pdfViewerSettings,
    templateDesignerModule,
    isPreview,
    setIsPreview,
    isReadOnlyMode,
    notSupported,
    ready,
  };
};
